import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AudioProvider } from './contexts/AudioContext';
import { FxProvider } from './contexts/FxContext';
import { DrumProvider } from './contexts/DrumContext';
import { InstrumentProvider } from './contexts/InstrumentContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import useVisualViewportHeight from './hooks/useVisualViewportHeight';
import useMobilePageZoomGuard from './hooks/useMobilePageZoomGuard';
import './App.css';
import ChatPlaybackEngine from './chat/ChatPlaybackEngine';
import MobileSiteLayout from './components/mobile/MobileSiteLayout';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const ChatGptCallbackPage = lazy(() => import('./pages/ChatGptCallbackPage'));
const SampleLabPage = lazy(() => import('./pages/SampleLabPage'));
const BeatIdPage = lazy(() => import('./pages/BeatIdPage'));
const OAuthConsentPage = lazy(() => import('./pages/OAuthConsentPage'));
const LaunchPage = lazy(() => import('./pages/LaunchPage'));
const SessionPage = lazy(() => import('./pages/SessionPage'));
const SharedSessionPage = lazy(() => import('./pages/SharedSessionPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const MarketplacePage = lazy(() => import('./pages/DiscoveryPage'));
const SellerStudioPage  = lazy(() => import('./pages/SellerStudioPage'));
const AudioEnginePage   = lazy(() => import('./pages/AudioEnginePage'));
const DemoPage          = lazy(() => import('./pages/DemoPage'));
const ProducerProfilePage = lazy(() => import('./pages/ProducerProfilePage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const PolicyPage = lazy(() => import('./pages/PolicyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const TrainingPracticePage = lazy(() => import('./pages/TrainingPracticePage'));
const AdminTrainingPage = lazy(() => import('./pages/AdminTrainingPage'));
const AdminComparisonsPage = lazy(() => import('./pages/AdminComparisonsPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));

function RootPage() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="app-loading">Loading…</div>;
  return isAuthenticated ? <HomePage /> : <LaunchPage />;
}

function App() {
  // Everything below is sized against the visible viewport, so a bar pinned to
  // the bottom stays on screen when the keyboard or the URL bar moves.
  useVisualViewportHeight();
  useMobilePageZoomGuard();

  return (
    <AuthProvider>
      <WebSocketProvider>
        <AudioProvider>
          <InstrumentProvider>
            <FxProvider>
              <DrumProvider>
                {window.__COOKUP_CHAT_PLAYER__ ? <ChatPlaybackEngine /> : (
                <Router>
                  <div className="App">
                    <Suspense fallback={<div className="app-loading">Loading…</div>}>
                      <MobileSiteLayout><Routes>
                        <Route path="/" element={<RootPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/auth/chatgpt/callback" element={<ChatGptCallbackPage />} />
                        <Route path="/oauth/consent" element={<OAuthConsentPage />} />
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/friends" element={<FriendsPage />} />
                        <Route path="/billing" element={<BillingPage />} />
                        <Route path="/marketplace" element={<MarketplacePage />} />
                        <Route path="/seller-studio" element={<SellerStudioPage />} />
                        <Route path="/producers/:username" element={<ProducerProfilePage />} />
                        <Route path="/users/:username" element={<UserProfilePage />} />
                        <Route path="/session/:sessionId" element={<SessionPage />} />
                        <Route path="/sample-lab" element={<SampleLabPage />} />
                        <Route path="/beat-id" element={<BeatIdPage />} />
                        <Route path="/share/:shareToken" element={<SharedSessionPage />} />
                        {/* Public guest demo: no account, no backend, resets on refresh. */}
                        <Route path="/demo" element={<DemoPage />} />
                        <Route path="/download/audio-engine" element={<AudioEnginePage />} />
                        <Route path="/terms" element={<PolicyPage policy="terms" />} />
                        <Route path="/privacy" element={<PolicyPage policy="privacy" />} />
                        <Route path="/copyright" element={<PolicyPage policy="copyright" />} />
                        <Route path="/community-rules" element={<PolicyPage policy="community-rules" />} />
                        <Route path="/account-data" element={<PolicyPage policy="account-data" />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/training" element={<TrainingPracticePage />} />
                        <Route path="/admin/training" element={<TrainingPracticePage />} />
                        <Route path="/training/compare" element={<TrainingPracticePage />} />
                        <Route path="/training/patterns" element={<TrainingPracticePage />} />
                        <Route path="/admin/training/compare" element={<AdminComparisonsPage />} />
                        <Route path="/admin/training/reviews" element={<AdminTrainingPage />} />
                        <Route path="/admin" element={<AdminDashboardPage />} />
                        <Route path="*" element={<Navigate to="/" />} />
                      </Routes></MobileSiteLayout>
                    </Suspense>
                  </div>
                </Router>
                )}
              </DrumProvider>
            </FxProvider>
          </InstrumentProvider>
        </AudioProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
